import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ReportMenuDTO,
  ReportDefinitionDTO,
  ReportExecutionResult,
  ViewMode,
} from '../types';

interface ReportStudioState {
  // Menu state
  menuData: ReportMenuDTO | null;
  menuLoading: boolean;
  menuError: string | null;

  // Selected report
  selectedReport: ReportMenuDTO | null;
  reportDefinition: ReportDefinitionDTO | null;

  // Execution state
  executionResult: ReportExecutionResult | null;
  isExecuting: boolean;
  executionError: string | null;

  // User preferences (persisted)
  favorites: number[];
  recentReports: ReportMenuDTO[];
  viewMode: ViewMode;

  // Filters
  searchQuery: string;
  selectedCategory: number | null;
}

interface ReportStudioActions {
  // Menu actions
  setMenuData: (data: ReportMenuDTO | null) => void;
  setMenuLoading: (loading: boolean) => void;
  setMenuError: (error: string | null) => void;

  // Report selection
  setSelectedReport: (report: ReportMenuDTO | null) => void;
  setReportDefinition: (definition: ReportDefinitionDTO | null) => void;

  // Execution actions
  setExecutionResult: (result: ReportExecutionResult | null) => void;
  setIsExecuting: (isExecuting: boolean) => void;
  setExecutionError: (error: string | null) => void;

  // Favorites
  toggleFavorite: (reportId: number) => void;
  isFavorite: (reportId: number) => boolean;

  // Recent reports
  addToRecent: (report: ReportMenuDTO) => void;
  clearRecent: () => void;

  // View mode
  setViewMode: (mode: ViewMode) => void;

  // Filters
  setSearchQuery: (query: string) => void;
  setSelectedCategory: (categoryId: number | null) => void;

  // Reset
  reset: () => void;
}

const initialState: ReportStudioState = {
  menuData: null,
  menuLoading: false,
  menuError: null,
  selectedReport: null,
  reportDefinition: null,
  executionResult: null,
  isExecuting: false,
  executionError: null,
  favorites: [],
  recentReports: [],
  viewMode: 'category',
  searchQuery: '',
  selectedCategory: null,
};

export const useReportStudioStore = create<ReportStudioState & ReportStudioActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Menu actions
      setMenuData: (data) => set({ menuData: data }),
      setMenuLoading: (loading) => set({ menuLoading: loading }),
      setMenuError: (error) => set({ menuError: error }),

      // Report selection
      setSelectedReport: (report) => set({ selectedReport: report }),
      setReportDefinition: (definition) => set({ reportDefinition: definition }),

      // Execution actions
      setExecutionResult: (result) => set({ executionResult: result }),
      setIsExecuting: (isExecuting) => set({ isExecuting: isExecuting }),
      setExecutionError: (error) => set({ executionError: error }),

      // Favorites
      toggleFavorite: (reportId) => {
        const { favorites } = get();
        if (favorites.includes(reportId)) {
          set({ favorites: favorites.filter((id) => id !== reportId) });
        } else {
          set({ favorites: [...favorites, reportId] });
        }
      },
      isFavorite: (reportId) => {
        return get().favorites.includes(reportId);
      },

      // Recent reports
      addToRecent: (report) => {
        const { recentReports } = get();
        // Remove if already exists
        const filtered = recentReports.filter((r) => r.id !== report.id);
        // Add to beginning, keep max 10
        set({ recentReports: [report, ...filtered].slice(0, 10) });
      },
      clearRecent: () => set({ recentReports: [] }),

      // View mode
      setViewMode: (mode) => set({ viewMode: mode }),

      // Filters
      setSearchQuery: (query) => set({ searchQuery: query }),
      setSelectedCategory: (categoryId) => set({ selectedCategory: categoryId }),

      // Reset
      reset: () => set({ ...initialState, favorites: get().favorites, recentReports: get().recentReports }),
    }),
    {
      name: 'report-studio-storage',
      partialize: (state) => ({
        favorites: state.favorites,
        recentReports: state.recentReports,
      }),
    }
  )
);
